import { BaseDetector, DetectorType } from '@src/slide/application/service/snap-line-service/detector/base';
import sortBy from 'lodash/sortBy';
import {
    LineType,
    MarginSnapLineType,
} from '@src/slide/application/service/snap-line-service/interface/line.interface';
import { EdgeType, Orient, OrientDirection } from '@src/slide/application/service/snap-line-service/interface/orient.interface';
import { RefSourceType } from '@src/slide/application/service/snap-line-service/interface/ref.interface';
import { OperationType } from '@src/slide/application/service/snap-line-service/interface/snap-line-service.interface';
import { getOppositeOrient } from '@src/slide/application/service/snap-line-service/util/movement';
import { isGreater } from '@src/slide/application/service/snap-line-service/util';
import {
    MIN_BETWEEN_MARGIN_SPACING,
    MIN_EXTEND_MARGIN_SPACING,
} from '@src/slide/application/service/snap-line-service/constant';
import type {
    IElementBaseModel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import type {
    IRefElement,
    IRefFrame,
    MarginRefGroup,
    MarginRefGroupWithOrientDirection,
    SymmetryRefGroup,
} from '@src/slide/application/service/snap-line-service/interface/ref.interface';
import type {
    IActiveMarginSnapGridLine,
    IActiveSnapGridLine,
    IAddMarginSnapLineOption,
    IMarginSnapGridLine,
} from '@src/slide/application/service/snap-line-service/interface/line.interface';

/**
 * 间距探测器
 * 包含: 1. 元素之间的间距探测 2. 页面对称间距探测
 */
export class MarginDetector extends BaseDetector {
    public type = DetectorType.DYNAMIC;
    public lastOperationType: OperationType;

    private referCoveredResultCache: Record<string, boolean> = {};
    private marginRefGroups: Record<Orient, Record<string, MarginRefGroup>> = {
        [Orient.HORIZONTAL]: {},
        [Orient.VERTICAL]: {},
    };

    private symmetryRefGroups: Record<Orient, Record<string, SymmetryRefGroup>> = {
        [Orient.HORIZONTAL]: {},
        [Orient.VERTICAL]: {},
    };

    public clearCache() {
        super.clearCache();

        this.resetCoveredResultCache();
        this.marginRefGroups = {
            [Orient.HORIZONTAL]: {},
            [Orient.VERTICAL]: {},
        };
        this.symmetryRefGroups = {
            [Orient.HORIZONTAL]: {},
            [Orient.VERTICAL]: {},
        };
    }

    protected getRefGrids() {
        const staticRefGrids = super.getRefGrids();

        const dynamicRefGrids = [];
        this.addBetweenReferMarginSnapLines(dynamicRefGrids);

        return [...staticRefGrids, ...dynamicRefGrids];
    }

    // collect 就是重新收集，需要清除缓存 group
    protected collectRefGrids(): IMarginSnapGridLine[] {
        this.resetMarginGroup();

        const marginSnapLines: IMarginSnapGridLine[] = [];
        this.addExtendMarginSnapLines(marginSnapLines);
        this.addSlideSymmetryMarginSnapLines(marginSnapLines);

        return marginSnapLines;
    }

    protected collectRefGridsEffect(refGrids: IMarginSnapGridLine[]) {
        this.collectRefGroups(refGrids);
    }

    protected processActiveSnapLines(activeSnapLines: IActiveSnapGridLine[]): IActiveSnapGridLine[] {
        // 如果页面对称吸附线和间距吸附线位置一样 且 同一方向
        // 为了简化吸附线显示过滤掉页面对称吸附线
        const mainActiveSnapLines = activeSnapLines.filter((snapLine) => {
            const isSymmetry = snapLine.source === RefSourceType.SLIDE;
            if (isSymmetry) {
                const isConflict = activeSnapLines.some((otherSnapLine) => {
                    const isNotSymmetry = otherSnapLine.source !== RefSourceType.SLIDE;
                    return isNotSymmetry && otherSnapLine.orient === snapLine.orient;
                });

                return !isConflict;
            }

            return true;
        });
        return this.relateEqualMarginGroups(mainActiveSnapLines as IActiveMarginSnapGridLine[]);
    }

    /**
     * 获取 move 下，探测元素转换的网格配置，resize只需要rect相关的，不需要 baseLine 相关的
     */
    protected getMoveModeDetectElementGridOptions() {
        return super.getMoveModeDetectElementGridOptions().filter(opt => opt.type !== LineType.BASE_LINE);
    }

    protected isTriggerAdsroption(elementGrid: IMarginSnapGridLine, refGrid: IMarginSnapGridLine, gap: number) {
        return gap <= refGrid.maxAdsorbGap && elementGrid.fromEdge === refGrid.adsorbEdge;
    }

    protected getGridOptionsByAnchor() {
        const [verticalGrid, horizontalGrid] = super.getGridOptionsByAnchor();
        const { detectFrame } = this.context;

        const anchorPoint = this.getResizeNodePositionOnSlide();
        const elementFrame = this.getRefFrame(detectFrame);
        // 如果在相应的方向，有比当前的锚点更外侧的锚点。在对应方向就不产生探测网格，因为锚点的位置不是outerFrame的位置
        // 比如在旋转后，另一个角比当前的 resizeNode高，就只做水平吸附而不做垂直吸附
        const isAnchorInsideWidthEdge = isGreater(anchorPoint.x, elementFrame.left)
            && isGreater(elementFrame.right, anchorPoint.x);
        const isAnchorInsideHeightEdge = isGreater(anchorPoint.y, elementFrame.top)
            && isGreater(elementFrame.bottom, anchorPoint.y);

        // 如果锚点在width边的内部，就只做水平吸附
        if (isAnchorInsideWidthEdge) {
            return [horizontalGrid];
        }

        // 如果锚点在height边的内部，就只做垂直吸附
        if (isAnchorInsideHeightEdge) {
            return [verticalGrid];
        }

        // 如果锚点都在最外侧（无旋转的情况，就做同时做水平、垂直方向的吸附）
        return [verticalGrid, horizontalGrid];
    }

    /**
     * 添加参照元素之间的间距探测网格线，
     * 位置关系为: Refer => Element => Refer
     */
    private addBetweenReferMarginSnapLines(marginSnapLines: IMarginSnapGridLine[]) {
        // 在元素两侧的 refer，按垂直和竖直方向分组
        const referList = this.departRefersWithOrient();

        // 根据分组结果，与 detectElement 计算出吸附线位置
        const snapLineOptions = [
            ...this.createSnapLineOptionsWithOppositeRefers(referList.top, referList.bottom, Orient.VERTICAL),
            ...this.createSnapLineOptionsWithOppositeRefers(referList.left, referList.right, Orient.HORIZONTAL),
        ];

        snapLineOptions.forEach(option => this.addBetweenMarginSnapLine(marginSnapLines, option));
    }

    /**
     * 在基于操作元素相对的 refers 间生成吸附线配置 (用于BetweenReferMarginSnapLines)
     */
    private createSnapLineOptionsWithOppositeRefers(
        frontRefs: IRefElement[],
        behindRefs: IRefElement[],
        referSymmetryOrient: Orient,
    ) {
        const snapLineOptions: IAddMarginSnapLineOption[] = [];
        frontRefs.forEach((frontRef) => {
            behindRefs.forEach((behindRef) => {
                const snapLineOption = this.context.operationType === OperationType.MOVE
                    ? this.createMoveModeSnapLineOption(frontRef, behindRef, referSymmetryOrient)
                    : this.createResizeModeSnapLineOption(frontRef, behindRef, referSymmetryOrient);
                if (snapLineOption) {
                    snapLineOptions.push(...snapLineOption);
                }
            });
        });

        return snapLineOptions;
    }

    /**
     * 创建在拖拽模式下的吸附线配置 (用于BetweenReferMarginSnapLines)
     */
    private createMoveModeSnapLineOption(
        frontRef: IRefElement,
        behindRef: IRefElement,
        referSymmetryOrient: Orient,
    ): IAddMarginSnapLineOption[] | void {
        const detectElementFrame = this.context.detectFrame;
        const frontFrame = this.getRefOuterFrame(frontRef);
        const behindFrame = this.getRefOuterFrame(behindRef);

        if (referSymmetryOrient === Orient.HORIZONTAL) {
            const detectWidth = detectElementFrame.width;
            const marginSpacing = (behindFrame.left - frontFrame.right - detectWidth) / 2;
            if (marginSpacing >= MIN_BETWEEN_MARGIN_SPACING) {
                return [{
                    frontRef,
                    behindRef,
                    position: marginSpacing + frontFrame.right,
                    margin: marginSpacing,
                    // 吸附线与元素的排列方向是垂直的
                    orient: getOppositeOrient(referSymmetryOrient),
                    adsorbEdge: EdgeType.LEFT,
                }];
            }
        }

        const detectHeight = detectElementFrame.height;
        const marginSpacing = (behindFrame.top - frontFrame.bottom - detectHeight) / 2;
        if (marginSpacing >= MIN_BETWEEN_MARGIN_SPACING) {
            return [{
                frontRef,
                behindRef,
                position: marginSpacing + frontFrame.bottom,
                margin: marginSpacing,
                // 吸附线与元素的排列方向是垂直的
                orient: getOppositeOrient(referSymmetryOrient),
                adsorbEdge: EdgeType.TOP,
            }];
        }
    }

    /**
     * 创建在resize模式下的吸附线配置(用于BetweenReferMarginSnapLines)
     */
    private createResizeModeSnapLineOption(
        frontRef: IRefElement,
        behindRef: IRefElement,
        referSymmetryOrient: Orient,
    ): IAddMarginSnapLineOption[] | void  {
        const { operationAnchorOnSlide, detectFrame } = this.context;

        const detectElementFrame = this.getRefFrame(detectFrame);
        const frontFrame = this.getRefOuterFrame(frontRef);
        const behindFrame = this.getRefOuterFrame(behindRef);
        const snapLineOptions: IAddMarginSnapLineOption[] = [];

        if (referSymmetryOrient === Orient.HORIZONTAL) {
            const isLeftSideAnchor = operationAnchorOnSlide.includes('left');
            const isRightSideAnchor = operationAnchorOnSlide.includes('right');

            if (isLeftSideAnchor || isRightSideAnchor) {
                const { marginSpacing, position } = this.calculateMarginSpacingAndPosition({
                    orient: referSymmetryOrient,
                    isLeftSideAnchor,
                    detectElementFrame,
                    frontFrame,
                    behindFrame,
                });

                if (marginSpacing >= MIN_BETWEEN_MARGIN_SPACING) {
                    snapLineOptions.push({
                        frontRef,
                        behindRef,
                        position,
                        margin: marginSpacing,
                        // 吸附线与元素的排列方向是垂直的
                        orient: getOppositeOrient(referSymmetryOrient),
                        adsorbEdge: isLeftSideAnchor ? EdgeType.LEFT : EdgeType.RIGHT,
                    });
                }
            }
        } else {
            const isTopSideAnchor = operationAnchorOnSlide.includes('top');
            const isBottomSideAnchor = operationAnchorOnSlide.includes('bottom');

            if (isTopSideAnchor || isBottomSideAnchor) {
                const { marginSpacing, position } = this.calculateMarginSpacingAndPosition({
                    orient: referSymmetryOrient,
                    isTopSideAnchor,
                    detectElementFrame,
                    frontFrame,
                    behindFrame,
                });

                if (marginSpacing >= MIN_BETWEEN_MARGIN_SPACING) {
                    snapLineOptions.push({
                        frontRef,
                        behindRef,
                        position,
                        margin: marginSpacing,
                        // 吸附线与元素的排列方向是垂直的
                        orient: getOppositeOrient(referSymmetryOrient),
                        adsorbEdge: isTopSideAnchor ? EdgeType.TOP : EdgeType.BOTTOM,
                    });
                }
            }
        }

        return snapLineOptions;
    }

    /**
     * 计算间距空间和吸附位置 (用于BetweenReferMarginSnapLines)
     */
    private calculateMarginSpacingAndPosition({
        orient,
        isTopSideAnchor,
        isLeftSideAnchor,
        detectElementFrame,
        frontFrame,
        behindFrame,
    }: { orient: Orient,
        isLeftSideAnchor?: boolean,
        isTopSideAnchor?: boolean,
        detectElementFrame: IRefFrame,
        frontFrame: IRefFrame,
        behindFrame: IRefFrame,
    }) {
        let marginSpacing = 0;
        let position = 0;
        if (orient === Orient.HORIZONTAL) {
            if (isLeftSideAnchor) {
                const staticEdgePosition = detectElementFrame.right;
                marginSpacing =  behindFrame.left - staticEdgePosition;
                position = frontFrame.right + marginSpacing;
            } else {
                const staticEdgePosition = detectElementFrame.left;
                marginSpacing = staticEdgePosition - frontFrame.right;
                position = behindFrame.left - marginSpacing;
            }

            return { marginSpacing, position };
        }

        if (isTopSideAnchor) {
            const staticEdgePosition = detectElementFrame.bottom;
            marginSpacing = behindFrame.top - staticEdgePosition;
            position = frontFrame.bottom + marginSpacing;
        } else {
            const staticEdgePosition = detectElementFrame.top;
            marginSpacing = staticEdgePosition - frontFrame.bottom;
            position = behindFrame.top - marginSpacing;
        }

        return { marginSpacing, position };
    }

    /**
     * 根据象限位置分离 refers
     */
    private departRefersWithOrient(needFilter = true) {
        const detectElementFrame = this.getRefFrame(this.context.detectFrame);
        const { refs } = this.dynamicRefer;
        const refers: MarginRefGroupWithOrientDirection = {
            [OrientDirection.TOP]: [],
            [OrientDirection.BOTTOM]: [],
            [OrientDirection.LEFT]: [],
            [OrientDirection.RIGHT]: [],
        };

        refs.forEach((ref) => {
            const refFrame = this.getRefOuterFrame(ref);
            // 动态 refers是一定有投影交集的，所以这里直接判断一边的边界是否无交集，另一边一定会有投影交集
            if (refFrame.bottom < detectElementFrame.top) {
                refers[OrientDirection.TOP].push(ref);
            } else if (refFrame.top > detectElementFrame.bottom) {
                refers[OrientDirection.BOTTOM].push(ref);
            } else if (refFrame.right < detectElementFrame.left) {
                refers[OrientDirection.LEFT].push(ref);
            } else if (refFrame.left > detectElementFrame.right) {
                refers[OrientDirection.RIGHT].push(ref);
            }
        });

        if (needFilter) {
            refers.top = this.filterCompleteCoveredRefer(refers.top, OrientDirection.TOP);
            refers.bottom = this.filterCompleteCoveredRefer(refers.bottom, OrientDirection.BOTTOM);
            refers.left = this.filterCompleteCoveredRefer(refers.left, OrientDirection.LEFT);
            refers.right = this.filterCompleteCoveredRefer(refers.right, OrientDirection.RIGHT);
        }

        return refers;
    }

    /**
     * 过滤掉被其他元素完全遮挡住的 refer
     *     1. 一旦有一个元素被完全前面的元素遮挡，会过滤该元素及后面所有的元素，即使后面有没被遮挡的元素也同样会被过滤掉
     *     2. 一旦有一个元素完全遮挡住了 detectElement，会过滤后面所有的元素（不包括该元素）
     */
    private filterCompleteCoveredRefer(refers: IRefElement[], orientDirection: OrientDirection) {
        if (refers.length === 0) return [];

        const validRefers: IRefElement[] = [];
        const orderedRefs = this.getOrderedRefsWithOrientDirection(refers, orientDirection);
        const detectElementFrame = this.getRefFrame(this.context.detectFrame);

        for (let i = 0; i < orderedRefs.length; i++) {
            const refer = orderedRefs[i];
            // 判断是否遮挡住 detectElement
            if (this.isShadowCoveredBy(refer, detectElementFrame, orientDirection)) {
                validRefers.push(refer);
                break;
            }

            // 判断是否遮挡住后面的 refer
            const nextRef = orderedRefs[i + 1];
            if (nextRef && this.isShadowCoveredBy(refer, nextRef, orientDirection)) {
                validRefers.push(refer);
                break;
            }

            validRefers.push(refer);
        }

        return validRefers;
    }

    /**
     * 获取按orientDirection升序排序的参照物，方便计算出离某个方向最靠近的边距
     */
    private getOrderedRefsWithOrientDirection(refers: IRefElement[], orientDirection: OrientDirection) {
        // left是以 right排序，right越小越近，需要 reverse。top同理
        if (orientDirection === OrientDirection.TOP || orientDirection === OrientDirection.LEFT) {
            return this.orderElementsWithOrientDirection(refers, orientDirection).reverse();
        }

        return this.orderElementsWithOrientDirection(refers, orientDirection);
    }

    /**
     * target 的投影是否被 ref 的投影遮挡
     */
    private isShadowCoveredBy(ref: IRefElement, target: IRefFrame | IRefElement, orientDirection: OrientDirection) {
        const refFrame = this.getRefOuterFrame(ref);
        const targetFrame = (target as IRefElement).ele
            ? this.getRefOuterFrame(target as IRefElement) : target as IRefFrame;
        if (orientDirection === OrientDirection.TOP || orientDirection === OrientDirection.BOTTOM) {
            return refFrame.left <= targetFrame.left && refFrame.right >= targetFrame.right;
        }

        return refFrame.top <= targetFrame.top && refFrame.bottom >= targetFrame.bottom;
    }

    /**
     * target 是否被 ref 的遮挡
     */
    private isCoveredBy(ref: IRefElement, target: IRefElement, orientDirection: OrientDirection) {
        const refFrame = this.getRefFrame(ref.outerFrameOnSlide);
        const targetFrame = this.getRefFrame(target.outerFrameOnSlide);

        const isVerticalCovered =  refFrame.top <= targetFrame.top && refFrame.bottom >= targetFrame.bottom;
        const isHorizontalCovered = refFrame.left <= targetFrame.left && refFrame.right >= targetFrame.right;

        if (orientDirection === OrientDirection.LEFT) {
            return refFrame.left <= targetFrame.left && isVerticalCovered;
        }

        if (orientDirection === OrientDirection.RIGHT) {
            return refFrame.right >= targetFrame.right && isVerticalCovered;
        }

        if (orientDirection === OrientDirection.TOP) {
            return refFrame.top <= targetFrame.top && isHorizontalCovered;
        }

        return refFrame.bottom >= targetFrame.bottom && isHorizontalCovered;
    }


    /**
     * 添加参照元素间延伸的间距探测网格线
     * 位置关系为: Refer => Refer => Element
     */
    private addExtendMarginSnapLines(marginSnapLines: IMarginSnapGridLine[]) {
        // 需要两个方向的吸附线原因是，即使我是水平移动，如果我当前吸住了竖直方向的参考线，我需要继续保持竖直方向的吸附，同时进行水平方向的吸附
        this.addExtendMarginSnapLinesWithOrient(this.dynamicRefer.refs, marginSnapLines, Orient.HORIZONTAL);
        this.addExtendMarginSnapLinesWithOrient(this.dynamicRefer.refs, marginSnapLines, Orient.VERTICAL);
    }

    /**
     * Slide画布对称探测网格线，基于画布基线对称
     */
    private addSlideSymmetryMarginSnapLines(marginSnapLines: IMarginSnapGridLine[]) {
        const dynamicRefs = this.context.dynamicRefer.refs;
        const { slideWidth, slideHeight } = this.context.uiDataService.presentation.props;
        // 线宽1，减1计算居中
        const horizontalBaseLinePosition = (slideHeight - 1) / 2;
        const verticalBaseLinePosition = (slideWidth - 1) / 2;
        const horizontalSlideRef = this.staticRefer.getSlideRef({
            height: 1,
            top: horizontalBaseLinePosition,
            left: 0,
        });
        const verticalSlideRef = this.staticRefer.getSlideRef({
            width: 1,
            top: 0,
            left: verticalBaseLinePosition,
        });

        dynamicRefs.forEach((ref) => {
            this.addSymmetrySnapLinesWithOrient(ref, horizontalSlideRef, Orient.HORIZONTAL, marginSnapLines);
            this.addSymmetrySnapLinesWithOrient(ref, verticalSlideRef, Orient.VERTICAL, marginSnapLines);
        });
    }

    /**
     * 添加页面对称吸附线
     */
    private addSymmetrySnapLinesWithOrient(
        ref: IRefElement,
        slideRef: IRefElement,
        orient: Orient,
        marginSnapLines: IMarginSnapGridLine[],
    ) {
        const { slideWidth, slideHeight } = this.context.uiDataService.presentation.props;
        const horizontalBaseLinePosition = slideHeight / 2;
        const verticalBaseLinePosition = slideWidth / 2;

        const frame = this.getRefOuterFrame(ref);
        if (orient === Orient.HORIZONTAL) {
            const isOverlappedWithBaseLine = frame.top <= horizontalBaseLinePosition
                && frame.bottom >= horizontalBaseLinePosition;
            if (isOverlappedWithBaseLine) {
                return;
            }

            const isRefAboveBaseLine = frame.bottom < horizontalBaseLinePosition;
            const margin = isRefAboveBaseLine ? horizontalBaseLinePosition - frame.bottom
                : horizontalBaseLinePosition - frame.top;

            this.addExtendMarginSnapLine(marginSnapLines, {
                frontRef: ref,
                behindRef: slideRef,
                position: horizontalBaseLinePosition + margin,
                orient: Orient.HORIZONTAL,
                adsorbEdge: isRefAboveBaseLine ? EdgeType.TOP : EdgeType.BOTTOM,
                margin,
                source: RefSourceType.SLIDE,
            });
        } else {
            const isOverlappedWithBaseLine = frame.left <= verticalBaseLinePosition
                && frame.right >= verticalBaseLinePosition;
            if (isOverlappedWithBaseLine) {
                return;
            }

            const isRefLeftToBaseLine = frame.right < verticalBaseLinePosition;
            const margin = isRefLeftToBaseLine ? verticalBaseLinePosition - frame.right
                : verticalBaseLinePosition - frame.left;
            this.addExtendMarginSnapLine(marginSnapLines, {
                frontRef: ref,
                behindRef: slideRef,
                position: verticalBaseLinePosition + margin,
                orient: Orient.VERTICAL,
                adsorbEdge: isRefLeftToBaseLine ? EdgeType.LEFT : EdgeType.RIGHT,
                margin,
                source: RefSourceType.SLIDE,
            });
        }
    }

    /**
     * 生成参照物在对应方向的间距（元素延伸间距）探测网格线
     * TODO：
     *  当前还存在一个遮挡策略还没有优化：
     *  extend参考线，元素被遮挡，但是非直接间距的情况下，会出现参考线，其实应该被遮挡元素穿过遮挡元素的间距参考线进行过滤
     */
    // eslint-disable-next-line complexity
    private addExtendMarginSnapLinesWithOrient(
        refers: IRefElement[],
        marginSnapLines: IMarginSnapGridLine[],
        movingOrient: Orient,
    ) {
        const orderedRefs = this.orderElementsWithOrient(refers, movingOrient);
        const isMovingHorizontal = movingOrient === Orient.HORIZONTAL;

        for (let frontIndex = 0; frontIndex < orderedRefs.length - 1; frontIndex++) {
            const frontRef = orderedRefs[frontIndex];
            const frontRefFrame = this.getRefOuterFrame(frontRef);

            for (let behindIndex = frontIndex + 1; behindIndex < orderedRefs.length; behindIndex++) {
                const behindRef = orderedRefs[behindIndex];
                const behindRefFrame = this.getRefOuterFrame(behindRef);

                if (!this.dynamicRefer.isShadowOverlapped(frontRef, behindRef)) {
                    continue;
                }

                if (isMovingHorizontal) {
                    const margin = behindRefFrame.left - frontRefFrame.right;
                    // 忽略负边距和无边距
                    if (margin > 0) {
                        if (!this.isCoveredByOtherRefers(frontRef, OrientDirection.LEFT)) {
                            // 左侧边距参考线
                            const leftMarginSnapLinePosition = frontRefFrame.left - margin;
                            this.addExtendMarginSnapLine(marginSnapLines, {
                                frontRef,
                                behindRef,
                                position: leftMarginSnapLinePosition,
                                orient: Orient.VERTICAL,
                                adsorbEdge: EdgeType.RIGHT,
                                margin,
                            });
                        }

                        if (!this.isCoveredByOtherRefers(behindRef, OrientDirection.RIGHT)) {
                            // 右侧边距参考线
                            const rightMarginSnapLinePosition = margin + behindRefFrame.right;
                            this.addExtendMarginSnapLine(marginSnapLines, {
                                frontRef,
                                behindRef,
                                position: rightMarginSnapLinePosition,
                                orient: Orient.VERTICAL,
                                adsorbEdge: EdgeType.LEFT,
                                margin,
                            });
                        }
                    }
                } else {
                    const margin = behindRefFrame.top - frontRefFrame.bottom;
                    // 忽略负边距和无边距
                    if (margin > 0) {
                        if (!this.isCoveredByOtherRefers(frontRef, OrientDirection.TOP)) {
                            // 顶部边距参考线
                            const topMarginSnapLinePosition = frontRefFrame.top - margin;
                            this.addExtendMarginSnapLine(marginSnapLines, {
                                frontRef,
                                behindRef,
                                position: topMarginSnapLinePosition,
                                orient: Orient.HORIZONTAL,
                                adsorbEdge: EdgeType.BOTTOM,
                                margin,
                            });
                        }

                        if (!this.isCoveredByOtherRefers(behindRef, OrientDirection.BOTTOM)) {
                            // 底部边距参考线
                            const bottomMarginSnapLinePosition = margin + behindRefFrame.bottom;
                            this.addExtendMarginSnapLine(marginSnapLines, {
                                frontRef,
                                behindRef,
                                position: bottomMarginSnapLinePosition,
                                orient: Orient.HORIZONTAL,
                                adsorbEdge: EdgeType.TOP,
                                margin,
                            });
                        }
                    }
                }
            }
        }
    }

    private isCoveredByOtherRefers(ref: IRefElement, orientDirection: OrientDirection) {
        const cache = this.getCoveredResultFromCache(ref, orientDirection);
        if (cache !== undefined) {
            return cache;
        }

        const result = this.dynamicRefer.refs.some((otherRef) => {
            if ((otherRef.ele as IElementBaseModel).key === (ref.ele as IElementBaseModel).key) {
                return false;
            }

            const isCovered =  this.isCoveredBy(otherRef, ref, orientDirection);
            const isSameOrientDirection = this.isSameOrientDirectionToDetectFrame(orientDirection, [otherRef, ref]);

            // 相对于 detectFrame 同方位，并且遮挡才算遮挡
            return isCovered && isSameOrientDirection;
        });

        this.setCoveredResultCache(ref, orientDirection, result);

        return result;
    }

    private isSameOrientDirectionToDetectFrame(orientDirection: OrientDirection, refs: IRefElement[]) {
        const detectFrame = this.getRefFrame(this.context.detectFrame);

        return refs.every((ref) => {
            const refFrame = this.getRefFrame(ref.outerFrameOnSlide);
            if (orientDirection === OrientDirection.LEFT) {
                return refFrame.right < detectFrame.left;
            }
            if (orientDirection === OrientDirection.TOP) {
                return refFrame.bottom < detectFrame.top;
            }
            if (orientDirection === OrientDirection.RIGHT) {
                return refFrame.left > detectFrame.right;
            }

            return refFrame.top > detectFrame.bottom;
        });
    }

    private getCoveredResultFromCache(ref: IRefElement, orientDirection: OrientDirection) {
        const key = (ref.ele as IElementBaseModel).key + orientDirection;
        return this.referCoveredResultCache[key];
    }

    private setCoveredResultCache(ref: IRefElement, orientDirection: OrientDirection, result: boolean) {
        const key = (ref.ele as IElementBaseModel).key + orientDirection;
        this.referCoveredResultCache[key] = result;
    }

    private resetCoveredResultCache() {
        this.referCoveredResultCache = {};
    }

    /**
     * 将参照物按 方向排序，方便后续做 两两边距计算
     */
    private orderElementsWithOrient(refs: IRefElement[], movingOrient: Orient) {
        return sortBy(refs, [(ref) => {
            const frame = this.getRefOuterFrame(ref);
            if (movingOrient === Orient.HORIZONTAL) {
                return frame.left || 0;
            }
            return frame.top || 0;
        }]);
    }

    /**
     * 将参照物按orientDirection升序排序，方便计算出离某个方向最靠近的边距
     */
    private orderElementsWithOrientDirection(refs: IRefElement[], orientDirection: OrientDirection) {
        return sortBy(refs, [(ref) => {
            const frame = this.getRefOuterFrame(ref);
            if (orientDirection === OrientDirection.TOP) {
                return frame.bottom || 0;
            }

            if (orientDirection === OrientDirection.BOTTOM) {
                return frame.top || 0;
            }

            if (orientDirection === OrientDirection.LEFT) {
                return frame.right || 0;
            }

            return frame.left || 0;
        }]);
    }

    /**
     * 增加间距参考线
     */
    private addExtendMarginSnapLine(
        marginSnapLines: IMarginSnapGridLine[],
        opt: IAddMarginSnapLineOption,
    ) {
        const { frontRef, behindRef, position, orient, adsorbEdge, source } = opt;

        // 页面对称间距会出现margin为负的情况，这里取绝对值
        if (Math.abs(opt.margin) < MIN_EXTEND_MARGIN_SPACING) {
            return;
        }

        const margin = opt.margin.toFixed(2);
        marginSnapLines.push({
            position,
            orient,
            maxAdsorbGap: this.context.option.maxAdsorbGap,
            type: LineType.MARGIN,
            source: source || RefSourceType.ELEMENT,
            refElements: [frontRef, behindRef],
            adsorbEdge,
            margin,
            marginType: MarginSnapLineType.EXTEND,
        });
    }

    private addBetweenMarginSnapLine(
        marginSnapLines: IMarginSnapGridLine[],
        opt: IAddMarginSnapLineOption,
    ) {
        const { frontRef, behindRef, position, orient, adsorbEdge, source } = opt;
        const margin = opt.margin.toFixed(2);

        marginSnapLines.push({
            position,
            orient,
            maxAdsorbGap: this.context.option.maxAdsorbGap,
            type: LineType.MARGIN,
            source: RefSourceType.ELEMENT,
            refElements: [frontRef, behindRef],
            adsorbEdge,
            margin,
            marginType: MarginSnapLineType.BETWEEN,
        });
    }

    private collectRefGroups(snapLines: IMarginSnapGridLine[]) {
        snapLines.forEach((snapLine) => {
            const { source, orient, margin, refElements, marginType } = snapLine;
            const [frontRef, behindRef] = refElements;
            if (source === RefSourceType.SLIDE) {
                this.collectSymmetryGroup(orient, margin, frontRef);
            } else {
                // MarginSnapLineType.Bewteen 是ref与 element间的，不参与收集
                // 两种type如果最终margin 相等，将会同时触发和显示
                if (marginType === MarginSnapLineType.EXTEND) {
                    this.collectMarginGroup(orient, margin, [frontRef, behindRef]);
                }
            }
        });
    }

    private getRefOuterFrame(ref: IRefElement): IRefFrame {
        const frame = ref.outerFrameOnSlide;
        return {
            ...frame,
            right: frame.left + frame.width,
            bottom: frame.top + frame.height,
        };
    }

    private collectMarginGroup(orient: Orient, margin: string, refElements: IRefElement[]) {
        if (!this.marginRefGroups[orient][margin]) {
            this.marginRefGroups[orient][margin] = [];
        }

        const marginGroup = this.marginRefGroups[orient][margin];
        if (!this.isGroupExisted(marginGroup, refElements)) {
            this.marginRefGroups[orient][margin].push(refElements as [IRefElement, IRefElement]);
        }
    }

    // margin区分正负，方便后续做对称聚合
    private collectSymmetryGroup(orient: Orient, margin: string, refElements: IRefElement) {
        if (!this.symmetryRefGroups[orient][margin]) {
            this.symmetryRefGroups[orient][margin] = [];
        }

        this.symmetryRefGroups[orient][margin].push(refElements);
    }

    private isGroupExisted(marginGroup: MarginRefGroup, refElements: IRefElement[]) {
        return marginGroup.some(group => refElements
            .every(ref => group
                .some((groupElement) => {
                    const groupElementKey = (groupElement.ele as IElementBaseModel).key;
                    const refElementKey = (ref.ele as IElementBaseModel).key;
                    return groupElementKey === refElementKey;
                })));
    }

    private relateEqualMarginGroups(activeSnapLines: IActiveMarginSnapGridLine[]): IActiveMarginSnapGridLine[] {
        return activeSnapLines.map((snapLine) => {
            const { margin, orient, source } = snapLine;
            const groups = source === RefSourceType.SLIDE ? this.symmetryRefGroups : this.marginRefGroups;

            const equalMarginRefGroup = groups[orient][margin] || [];
            return {
                ...snapLine,
                equalMarginRefGroup,
            };
        });
    }

    private resetMarginGroup() {
        this.marginRefGroups.horizontal = {};
        this.marginRefGroups.vertical = {};
        this.symmetryRefGroups.horizontal = {};
        this.symmetryRefGroups.vertical = {};
    }
}
